/** Dashboard geral: panorama de todos os clientes (só leitura). Fase 1. */
import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { ClientDTO } from "@unik/shared";
import { api } from "../../lib/api";
import { fmtInt, fmtGrowthPerMonth } from "../../lib/metrics";
import { AdminShell } from "../../ui/AdminShell";
import { Card, Skeleton } from "../../ui/Card";

export function DashboardGeral() {
  const [clients, setClients] = useState<ClientDTO[] | null>(null);

  useEffect(() => {
    api.get<ClientDTO[]>("/clients").then(setClients).catch(() => setClients([]));
  }, []);

  if (clients === null) return <AdminShell><Skeleton className="h-40" /></AdminShell>;

  const sum = (pick: (c: ClientDTO) => number): number => clients.reduce((s, c) => s + pick(c), 0);
  const cards: [string, number][] = [
    ["Clientes", clients.length],
    ["Aguardando aprovação", sum((c) => c.stats.pendingApproval)],
    ["Aprovados", sum((c) => c.stats.approved)],
    ["Publicados", sum((c) => c.stats.published)],
  ];
  const attention = clients.filter((c) => c.stats.pendingApproval > 0);

  return (
    <AdminShell>
      <h1 className="type-h1 text-text-cream">Dashboard</h1>
      <p className="type-body-sm mt-1 text-text-secondary">Panorama geral dos seus clientes.</p>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map(([label, value]) => (
          <Card key={label} className="text-center">
            <p className="type-h1 text-text-cream">{value}</p>
            <p className="type-caption mt-1 text-text-tertiary">{label}</p>
          </Card>
        ))}
      </div>

      {attention.length > 0 && (
        <Card className="mt-4">
          <h2 className="type-h3 text-text-cream">Precisam de você</h2>
          <div className="mt-3 flex flex-col gap-2">
            {attention.map((c) => (
              <Link
                key={c.id} to={`/clientes/${c.id}/kanban`}
                className="flex items-center justify-between rounded-[var(--radius-control-sm)] border border-border-subtle px-3 py-2 hover:border-border-strong"
              >
                <span className="type-body-sm text-text-cream">{c.name}</span>
                <span className="type-caption text-warning">{c.stats.pendingApproval} aguardando</span>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <h2 className="type-h3 mt-8 text-text-cream">Por cliente</h2>
      {clients.length === 0 ? (
        <p className="type-body-sm mt-3 text-text-tertiary">Nenhum cliente ainda. Comece pelo Cadastro.</p>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {clients.map((c) => {
            const g = c.growth;
            const semDados = g.followersCurrent === null && g.storyViewsCurrent === null;
            return (
              <Link
                key={c.id} to={`/clientes/${c.id}/dashboard`}
                className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-border-subtle bg-surface-raised px-4 py-3 hover:border-border-strong sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="type-body text-text-cream">{c.name}</span>
                {semDados ? (
                  <span className="type-caption text-text-tertiary">Sem números registrados ainda →</span>
                ) : (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 type-caption text-text-tertiary">
                    <span>
                      <span className="text-text-cream">
                        {g.followersCurrent === null ? "—" : fmtInt(g.followersCurrent)}
                      </span>{" "}
                      seguidores · {fmtGrowthPerMonth(g.followersGrowthPerMonth)}
                    </span>
                    <span>
                      stories:{" "}
                      <span className="text-text-cream">
                        {g.storyViewsCurrent === null ? "—" : fmtInt(g.storyViewsCurrent)}
                      </span>
                      {g.storyViewsAverage !== null && ` (méd. ${fmtInt(g.storyViewsAverage)})`}
                    </span>
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
}
